import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Paper,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  IconButton,
  styled,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DeleteIcon from '@mui/icons-material/Delete'

import {
  getSource,
  getFileDataType,
  downloadTemplate,
  uploadFile,
} from '../../../api/upload.js'
import { useSnackbar } from '../../../contexts/SnackbarProvider'
import { GLOBAL_CONSTANTS } from '../../../constants/globalConstants'
import { sanitizeForLog } from '../../../constants/index'
import { useSelector } from 'react-redux'
import { ADMIN } from '../../../utils/role-capabilities.js'
import CustomSelect from '../CustomSelect'
import CommonModal from '../../common/CommonModal'

const StyledPaper = styled(Paper)(({ theme }) => ({
  border: '2px dashed #AAAAAA',
  borderRadius: '8px',
  padding: theme.spacing(1.5),
  backgroundColor: '#ebebeb',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#999',
    backgroundColor: '#f5f5f5',
  },
  '&.dragover': {
    borderColor: '#ebebeb',
    backgroundColor: '#ebebeb',
  },
  display: 'flex',
  flexDirection: 'column', // stack files vertically
  gap: 4,
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  maxHeight: '250px', // max height before scrolling
  overflowY: 'auto', // scroll when too many files
  width: '100%',
}))

/**
 * Check if the selected source starts with "Alt-" (case-insensitive)
 * These sources support multiple Excel file uploads.
 */
const isAltSource = (source) => {
  return source?.value?.toLowerCase().startsWith('alt-')
}

const FileUploadUI = ({ fetchMetaData, onSourcesLoaded, onDataTypesLoaded }) => {
  const userRole = useSelector(state => state.user.role)
  const isFieldsDisabled = userRole === ADMIN
  const [source, setSource] = useState(null)
  const [dataType, setDataType] = useState('')
  const [isDragOver, setIsDragOver] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([])
  const [uploadDetails, setUploadDetails] = useState({
    sources: [],
    types: [],
  })
  const [isDownloading, setIsDownloading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const { showSnackbar } = useSnackbar()

  useEffect(() => {
    Promise.all([getSource(), getFileDataType()])
      .then(([sources, types]) => {
        setUploadDetails({ sources: sources || [], types: types || [] })
        onSourcesLoaded?.(sources || [])
        onDataTypesLoaded?.(types || [])
        if (sources?.length > 0)
          setSource(sources.find(s => s.id === 0) || sources[0])
        if (types?.length > 0) setDataType(types[0])
      })
      .catch(err => {
        console.error(
          'Error fetching data:',
          sanitizeForLog(err?.message || 'Unknown error')
        )
      })
  }, [])

  const isPdfOnlyType = dataType =>
    dataType === 'Vendor LDC Pdf' || dataType === 'TDS Challan Pdf'

  const handleDragOver = e => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = e => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = e => {
    e.preventDefault()
    setIsDragOver(false)
    const files = Array.from(e.dataTransfer.files)
    handleFilesValidation(files)
  }

  const handleFileSelect = e => {
    const files = Array.from(e.target.files)
    handleFilesValidation(files)
  }

  const handleFilesValidation = files => {
    const lowerFiles = files.map(f => f.name.toLowerCase())

    // Alt sources support multiple Excel file uploads for specific data types
    const altSupportedTypes = ['Vendor Master', 'Vendor LDC Master', 'Vendor Transactional Data', 'TDS Challan Details']
    if (isAltSource(source)) {
      if (!altSupportedTypes.includes(dataType)) {
        showSnackbar('Multi-file upload is only supported for Vendor Master, Vendor LDC Master, Vendor Transactional Data, and TDS Challan Details', 'error')
        return
      }

      if (lowerFiles.some(name => name.endsWith('.pdf'))) {
        showSnackbar('PDF upload is not supported for Alt sources. Only Excel files are allowed.', 'error')
        return
      }

      const newExcelFiles = files.filter(
        f =>
          f.name.toLowerCase().endsWith('.xls') ||
          f.name.toLowerCase().endsWith('.xlsx')
      )

      if (newExcelFiles.length === 0) {
        showSnackbar('Please select Excel files (.xls or .xlsx)', 'error')
        return
      }

      // Append new files to existing selection (avoid duplicates by name)
      const existingNames = new Set(selectedFiles.map(f => f.name.toLowerCase()))
      const uniqueNewFiles = newExcelFiles.filter(
        f => !existingNames.has(f.name.toLowerCase())
      )
      setSelectedFiles(prev => [...prev, ...uniqueNewFiles])
      return
    }

    if (isPdfOnlyType(dataType)) {
      if (
        lowerFiles.some(name => name.endsWith('.xls') || name.endsWith('.xlsx'))
      ) {
        showSnackbar('Only PDF files are allowed for this Data Type', 'error')
        return
      }

      const newPdfFiles = files.filter(f =>
        f.name.toLowerCase().endsWith('.pdf')
      )
      const existingPdfFiles = selectedFiles.filter(f =>
        f.name.toLowerCase().endsWith('.pdf')
      )

      const combinedPdfFiles = [...existingPdfFiles, ...newPdfFiles]

      const totalPdfSizeMB =
        combinedPdfFiles.reduce((sum, f) => sum + f.size, 0) / (1024 * 1024)

      if (totalPdfSizeMB > GLOBAL_CONSTANTS.MAX_TOTAL_SIZE_MB) {
        showSnackbar(
          `Total PDF size cannot exceed ${GLOBAL_CONSTANTS.MAX_TOTAL_SIZE_MB} MB`,
          'error'
        )
        return
      }

      setSelectedFiles(combinedPdfFiles)
      return
    }

    if (lowerFiles.some(name => name.endsWith('.pdf'))) {
      showSnackbar('PDF upload is not allowed for this Data Type', 'error')
      return
    }

    const newExcelFiles = files.filter(
      f =>
        f.name.toLowerCase().endsWith('.xls') ||
        f.name.toLowerCase().endsWith('.xlsx')
    )

    // Non-Alt sources: only one Excel file allowed
    if (newExcelFiles.length > 1) {
      showSnackbar('Only one Excel file can be uploaded', 'error')
      return
    }

    const existingExcel = selectedFiles.some(
      f =>
        f.name.toLowerCase().endsWith('.xls') ||
        f.name.toLowerCase().endsWith('.xlsx')
    )

    if (existingExcel && newExcelFiles.length > 0) {
      showSnackbar('Only one Excel file can be uploaded', 'error')
      return
    }

    if (newExcelFiles.length === 1) {
      setSelectedFiles([newExcelFiles[0]])
    }
  }

  const handleBrowseFiles = () => {
    const fileInput = document.getElementById('file-input')
    if (fileInput) {
      // Reset value so the same file can be re-selected and the change event fires
      fileInput.value = ''
      fileInput.click()
    }
  }

  const handleDownloadTemplate = async () => {
    if (!dataType) return
    try {
      setIsDownloading(true)
      await downloadTemplate(dataType)
      showSnackbar('File downloaded successfully', 'success')
    } catch (err) {
      console.log(
        'Download failed:',
        sanitizeForLog(err?.message || 'Unknown error')
      )
    } finally {
      setIsDownloading(false)
    }
  }

  const handleUploadClick = async () => {
    if (!selectedFiles.length) {
      showSnackbar('Please select a file to upload', 'error')
      return
    }
    if (!source?.value || !dataType) {
      showSnackbar('Please select source and data type', 'error')
      return
    }
    if (dataType === 'Section Keyword' && source.value === 'Generic') {
      showSnackbar(
        'Generic source is not allowed for Section Keyword. Please select Reload.',
        'error'
      )
      return
    }
    if (
      dataType === 'Section Keyword' &&
      selectedFiles.some(f => f.name.toLowerCase().endsWith('.pdf'))
    ) {
      showSnackbar('PDF upload is not allowed for Section Keyword', 'error')
      return
    }

    try {
      showSnackbar('Uploading files...', 'info')
      setIsUploading(true)

      const isExcel =
        selectedFiles[0].name.toLowerCase().endsWith('.xls') ||
        selectedFiles[0].name.toLowerCase().endsWith('.xlsx')

      if (isExcel && isAltSource(source)) {
        // Multiple Excel files for Alt-* sources: send all files in a single API call
        const resp = await uploadFile({
          file: selectedFiles,
          source: source.value,
          dataType,
          loadType: 'Web Based',
          transformId: source.id,
        })
        if (resp?.success) {
          fetchMetaData()
          showSnackbar('Files uploaded successfully', 'success')
          setSelectedFiles([])
        } else {
          const errorMsg = resp?.errors?.[0]?.message || 'File upload failed'
          showSnackbar(errorMsg, 'error')
        }
      } else if (isExcel) {
        // Single Excel file upload (non-Alt source)
        const resp = await uploadFile({
          file: selectedFiles[0],
          source: source.value,
          dataType,
          loadType: 'Web Based',
          transformId: source.id,
        })
        if (resp?.success) {
          fetchMetaData()
          showSnackbar('File uploaded successfully', 'success')
          setSelectedFiles([])
        } else {
          const errorMsg = resp?.errors?.[0]?.message || 'File upload failed'
          showSnackbar(errorMsg, 'error')
        }
      } else {
        // Upload PDFs sequentially
        for (const file of selectedFiles) {
          const resp = await uploadFile({
            file,
            source: source.value,
            dataType,
            loadType: 'Web Based',
            transformId: source.id,
          })
          if (!resp?.success) {
            const errorMsg = resp?.errors?.[0]?.message || 'File upload failed'
            showSnackbar(errorMsg, 'error')
            return
          }
        }
        fetchMetaData()
        showSnackbar('Files uploaded successfully', 'success')
        setSelectedFiles([])
      }
    } catch (err) {
      console.error(sanitizeForLog(err?.message || 'Unknown upload error'))
      showSnackbar(err.message || 'File upload failed', 'error')
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemoveFile = (fileToRemove, e) => {
    if (e) e.stopPropagation()
    setSelectedFiles(prev => prev.filter(f => f !== fileToRemove))
  }

  const handleRemoveFileFromModal = fileToRemove => {
    setSelectedFiles(prev => {
      const updated = prev.filter(f => f !== fileToRemove)
      if (updated.length === 0) {
        setViewModalOpen(false)
      }
      return updated
    })
  }

  useEffect(() => {
    if (!selectedFiles.length) return

    const isPdfMode =
      dataType === 'Vendor LDC Pdf' || dataType === 'TDS Challan Pdf'

    const hasExcel = selectedFiles.some(
      f =>
        f.name.toLowerCase().endsWith('.xls') ||
        f.name.toLowerCase().endsWith('.xlsx')
    )

    const hasPdf = selectedFiles.some(f =>
      f.name.toLowerCase().endsWith('.pdf')
    )

    if (isPdfMode && hasExcel) {
      setSelectedFiles([])
      showSnackbar(
        'Excel file removed. Only PDF files are allowed for this Data Type.',
        'info'
      )
    } else if (!isPdfMode && hasPdf) {
      setSelectedFiles([])
      showSnackbar(
        'PDF files removed. Only Excel file is allowed for this Data Type.',
        'info'
      )
    }
  }, [dataType])

  const filteredTypes = uploadDetails?.types?.filter(
    type => source?.value === 'Reload' || type !== 'Section Keywords'
  ) || []

  return (
    <Box sx={{ p: '16px 24px', background: '#ffffff' }}>
      {/* Header */}
      <Box className="flex pb-4 justify-between items-center">
        <Typography
          className="font-normal text-black leading-[22px]"
          variant="h5"
          sx={{
            mb: 3,
            fontFamily: 'ch',
            fontSize: '18px',
            fontWeight: 'normal',
            lineHeight: '22px',
          }}
        >
          Upload
        </Typography>
        <Alert
          severity="warning"
          icon={<WarningAmberOutlinedIcon color="#EB8C00" fontSize="small" />}
          sx={{
            fontSize: '12px',
            background: '#FFF7C5',
            borderRadius: '4px',
            color: '#EB8C00',
          }}
        >
          Please do not upload more than 2,00,000 line items in a single file.
          Subsequent file should be uploaded once the previous file completes
          the validation checks.
        </Alert>
      </Box>

      {/* Main Upload Area */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
        }}
      >
        {/* Drag & Drop Section */}
        <Box sx={{ flex: 1 }}>
          <StyledPaper
            className={isDragOver ? 'dragover' : ''}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleBrowseFiles}
            sx={{
              height: '50px', // 👈 force fixed height for upload section
              overflow: 'hidden', // prevent parent stretching
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                height: '50px',
                overflow: 'hidden',
              }}
            >
              {selectedFiles.length ? (
                <>
                  {/* Show first file chip */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      backgroundColor: '#f4f4f4',
                      borderRadius: '4px',
                      px: 1,
                      py: 0.3,
                      flexShrink: 0, // don’t shrink filename chips
                      maxWidth: '220px', // truncate long names
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="success.main"
                      sx={{
                        fontSize: '13px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={selectedFiles[0].name}
                    >
                      {selectedFiles[0].name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation()
                        handleRemoveFile(selectedFiles[0], e)
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Show count badge and view icon for multiple files */}
                  {selectedFiles.length > 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={`+${selectedFiles.length - 1} more`}
                        size="small"
                        sx={{
                          fontSize: '12px',
                          backgroundColor: '#e0e0e0',
                          height: '24px',
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={e => {
                          e.stopPropagation()
                          setViewModalOpen(true)
                        }}
                        title="View all selected files"
                        sx={{
                          color: '#D93954',
                          '&:hover': { backgroundColor: '#fce4ec' },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body1" sx={{ fontSize: '14px' }}>
                  Drag & drop files in this area or
                  <Typography
                    component="span"
                    sx={{
                      color: '#d93954',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      pl: 1,
                      fontSize: '14px',
                    }}
                  >
                    Browse Files
                  </Typography>
                </Typography>
              )}
            </Box>
          </StyledPaper>

          <input
            id="file-input"
            type="file"
            hidden
            onChange={handleFileSelect}
            accept={isAltSource(source) ? '.xlsx,.xls' : '.csv,.xlsx,.xls,.pdf'}
            multiple={isAltSource(source) || isPdfOnlyType(dataType)}
          />
        </Box>

        {/* Controls */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
        
          <CustomSelect
            // label="Source"
            value={source?.value || ''}
            onChange={e => {
              const selected = uploadDetails.sources.find(s => s.value === e.target.value)
              setSource(selected)
              setSelectedFiles([])
              if (e.target.value === 'Generic' && dataType === 'Section Keyword') {
                setDataType('')
              }
            }}
            options={uploadDetails?.sources || []}
            getOptionLabel={(opt) => opt.value}
            getOptionValue={(opt) => opt.value}
            disabled={isFieldsDisabled}
            placeholder="Select Source"
          />

        

          <CustomSelect
            // label="Data Type"
            value={dataType}
            onChange={e => setDataType(e.target.value)}
            options={filteredTypes}
            placeholder="Select Data Type"
            disabled={isFieldsDisabled}
          />
          <Button
            variant="text"
            sx={{ fontSize: '14px', textTransform: 'none', color:  'var(--forbrand-primary)'}}
            onClick={handleDownloadTemplate}
            disabled={isDownloading || !dataType}
          >
            Download Template
          </Button>

          <Button
            variant="contained"
            sx={{
              textTransform: 'none',
              height: '30px',
              p: '8px 20px',
               backgroundColor: 'var(--forbrand-bg-variant)',
              color:'var(--forbrand-brand-varient)',
            }}
            onClick={handleUploadClick}
            disabled={!selectedFiles.length || isUploading}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Box>
      </Box>

      {/* View Files Modal */}
      <CommonModal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        title={`Selected Files (${selectedFiles.length})`}
        maxWidth="sm"
        fullWidth
      >
        <List dense>
          {selectedFiles.map((file, index) => (
            <ListItem
              key={`${file.name}-${index}`}
              sx={{
                borderBottom: '1px solid #f0f0f0',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <ListItemText
                primary={file.name}
                secondary={`${(file.size / 1024).toFixed(1)} KB`}
                primaryTypographyProps={{ fontSize: '14px' }}
                secondaryTypographyProps={{ fontSize: '12px' }}
              />
              <IconButton
                edge="end"
                size="small"
                onClick={() => handleRemoveFileFromModal(file)}
                title="Remove file"
              >
                <DeleteIcon fontSize="small" color="error" />
              </IconButton>
            </ListItem>
          ))}
        </List>
      </CommonModal>
    </Box>
  )
}

FileUploadUI.propTypes = {
  fetchMetaData: PropTypes.func.isRequired,
  onSourcesLoaded: PropTypes.func,
  onDataTypesLoaded: PropTypes.func,
}

export default FileUploadUI
